import React from 'react';
import { Link } from 'react-router-dom';
import { Minus, Plus, X, ShoppingBag, Loader2 } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { toast } from 'sonner';

export const CartSlideout = () => {
  const {
    cart,
    isCartOpen,
    setIsCartOpen,
    isLoading,
    updateCartLine,
    removeFromCart,
  } = useCart();

  const handleUpdateQuantity = async (lineId, currentQty, delta) => {
    const newQty = currentQty + delta;
    if (newQty < 1) {
      handleRemove(lineId);
      return;
    }
    try {
      await updateCartLine(lineId, newQty);
    } catch (err) {
      toast.error('Failed to update quantity');
    }
  };

  const handleRemove = async (lineId) => {
    try {
      await removeFromCart(lineId);
      toast.success('Item removed from cart');
    } catch (err) {
      toast.error('Failed to remove item');
    }
  };

  const handleCheckout = () => {
    if (cart?.checkoutUrl) {
      window.location.href = cart.checkoutUrl;
    }
  };

  const subtotal = parseFloat(cart?.subtotal || 0).toFixed(2);
  const isEmpty = !cart?.lines || cart.lines.length === 0;

  return (
    <Sheet open={isCartOpen} onOpenChange={setIsCartOpen}>
      <SheetContent
        side="right"
        className="w-full sm:w-[420px] bg-wild-sand flex flex-col p-0"
        data-testid="cart-slideout"
      >
        <SheetHeader className="px-6 py-4 border-b border-wild-khaki">
          <SheetTitle className="font-headings text-2xl tracking-wider text-wild-dark flex items-center gap-2">
            <ShoppingBag className="h-6 w-6" />
            YOUR CART
            {cart?.lines?.length > 0 && (
              <span className="text-wild-stone font-body text-base">
                ({cart.lines.reduce((sum, l) => sum + l.quantity, 0)} items)
              </span>
            )}
          </SheetTitle>
        </SheetHeader>

        {isEmpty ? (
          <div className="flex-1 flex flex-col items-center justify-center px-6">
            <ShoppingBag className="h-16 w-16 text-wild-khaki mb-4" />
            <p className="font-body text-wild-stone text-lg mb-6">
              Your cart is empty
            </p>
            <Button
              onClick={() => setIsCartOpen(false)}
              asChild
              className="bg-wild-leather text-white hover:bg-wild-leather/90 font-headings tracking-wider"
            >
              <Link to="/shop" data-testid="continue-shopping-btn">
                CONTINUE SHOPPING
              </Link>
            </Button>
          </div>
        ) : (
          <>
            <ScrollArea className="flex-1 px-6 py-4">
              <div className="space-y-4">
                {cart.lines.map((item) => (
                  <div
                    key={item.id}
                    className="flex gap-4 py-4 border-b border-wild-khaki/50"
                    data-testid={`cart-item-${item.id}`}
                  >
                    {/* Product Image */}
                    <div className="w-20 h-20 bg-wild-khaki/30 flex-shrink-0">
                      {item.image ? (
                        <img
                          src={item.image}
                          alt={item.productTitle}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag className="h-8 w-8 text-wild-stone" />
                        </div>
                      )}
                    </div>

                    {/* Product Details */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-headings text-sm tracking-wider text-wild-dark truncate">
                        {item.productTitle}
                      </h4>
                      {item.variantTitle && item.variantTitle !== 'Default Title' && (
                        <p className="font-body text-xs text-wild-stone mt-0.5">
                          {item.variantTitle}
                        </p>
                      )}
                      <p className="font-body font-semibold text-wild-dark mt-1">
                        ${parseFloat(item.price).toFixed(2)}
                      </p>

                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2 mt-2">
                        <button
                          onClick={() =>
                            handleUpdateQuantity(item.id, item.quantity, -1)
                          }
                          disabled={isLoading}
                          className="w-7 h-7 flex items-center justify-center border border-wild-khaki hover:border-wild-leather transition-colors"
                          data-testid={`decrease-qty-${item.id}`}
                        >
                          <Minus className="h-3 w-3" />
                        </button>
                        <span className="font-body text-sm w-8 text-center">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() =>
                            handleUpdateQuantity(item.id, item.quantity, 1)
                          }
                          disabled={isLoading}
                          className="w-7 h-7 flex items-center justify-center border border-wild-khaki hover:border-wild-leather transition-colors"
                          data-testid={`increase-qty-${item.id}`}
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemove(item.id)}
                      disabled={isLoading}
                      className="text-wild-stone hover:text-wild-dark transition-colors"
                      data-testid={`remove-item-${item.id}`}
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            </ScrollArea>

            <SheetFooter className="px-6 py-4 border-t border-wild-khaki bg-white">
              <div className="w-full space-y-4">
                {/* Subtotal */}
                <div className="flex items-center justify-between">
                  <span className="font-headings text-lg tracking-wider">
                    SUBTOTAL
                  </span>
                  <span className="font-body text-xl font-semibold">
                    ${subtotal}
                  </span>
                </div>
                <p className="font-body text-xs text-wild-stone">
                  Shipping & taxes calculated at checkout
                </p>

                <Separator className="bg-wild-khaki" />

                {/* Checkout Button */}
                <Button
                  onClick={handleCheckout}
                  disabled={isLoading || isEmpty}
                  className="w-full bg-wild-leather text-white hover:bg-wild-leather/90 py-6 text-lg font-headings tracking-widest"
                  data-testid="checkout-btn"
                >
                  {isLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : null}
                  CHECKOUT
                </Button>

                {/* Continue Shopping */}
                <Button
                  variant="outline"
                  onClick={() => setIsCartOpen(false)}
                  className="w-full border-wild-leather text-wild-leather hover:bg-wild-leather hover:text-white font-headings tracking-wider"
                  data-testid="continue-shopping-cart-btn"
                >
                  CONTINUE SHOPPING
                </Button>
              </div>
            </SheetFooter>
          </>
        )}

        {/* Loading Overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/50 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-wild-leather" />
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
